import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import CatalogFilterRail, { type CatalogFilterState } from "./CatalogFilterRail";
import type { CatalogFacetCounts } from "@/lib/cdnProducts";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: CatalogFilterState;
  onApply: (next: CatalogFilterState) => void;
  facets?: CatalogFacetCounts | undefined;
  resultCount: number;
  hideCategory?: boolean | undefined;
  hideVendor?: boolean | undefined;
}

/** Mobile-only popup for catalog filters. Selections stage locally and apply
 *  when the sticky footer button is tapped. */
export default function CatalogFilterSheet({
  open,
  onOpenChange,
  value,
  onApply,
  facets,
  resultCount,
  hideCategory,
  hideVendor,
}: Props) {
  const [draft, setDraft] = useState<CatalogFilterState>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-sm flex-col gap-0 p-0 sm:max-w-sm"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <SheetTitle className="font-heading text-base font-bold text-foreground">
            Filters
          </SheetTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close filters"
            className="rounded-sm p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          <CatalogFilterRail
            value={draft}
            onChange={setDraft}
            {...(facets !== undefined ? { facets } : {})}
            {...(hideCategory !== undefined ? { hideCategory } : {})}
            {...(hideVendor !== undefined ? { hideVendor } : {})}
          />
        </div>

        <div
          className="border-t border-border bg-background p-3"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <Button
            className="h-12 w-full text-base"
            onClick={() => {
              onApply(draft);
              onOpenChange(false);
            }}
          >
            Show {resultCount} result{resultCount === 1 ? "" : "s"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}